import { Component, Prop, State, Element, Watch, h } from '@stencil/core';

@Component({
  tag: 'cbssports-newsletter',
  styleUrl: 'index.css',
  shadow: true
})
export class CBSSportsNewsletter {
  @Element() el: HTMLElement;

  @Prop() mpid: number;
  @Prop() source: string;
  @Prop() mCodes: string;
  @Prop() local?: boolean;
  @Prop() baseUrl?: string;
  @Prop() dataType?: string;
  @Prop() mobileApp?: boolean;

  @State() emailAddress = '';
  @State() isSubscribed = false;
  @State() isSubscribedAll = false;
  @State() currentMCode = '';
  @State() checked = false;
  @State() loading = true;
  @State() error = false;
  @State() infoError = false;
  @State() signUpError = false;
  @State() fetched = false;

  @State() userSubscriptions: {
    subscriptions: { mCode: string }[];
  } = {
    subscriptions: []
  };
  @State() newsletterInfo: {
    title: string;
    description: string;
    label: string;
    author: string;
    image: string;
  } = {
    author: '',
    description: '',
    image: '',
    label: '',
    title: ''
  };
  @State() disclosureInfo: {
    disclosureId: number;
    disclosureText: string;
    requiredInd?: number;
  }[] = [];

  private baseUrlValue: string;

  private getCookieValue(name: string) {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
      const [key, value] = cookie.split('=');
      if (key === name) {
        return value;
      }
    }
    return null;
  }

  private async getUserSubscriptions() {
    try {
      const response = await fetch(
        `${this.baseUrlValue}/api/authex/user/newsletters`,
        {
          method: 'GET',
          credentials: 'include',
          mode: 'cors'
        }
      );
      if (!response.ok) {
        return null;
      }
      const result = await response.json();
      this.userSubscriptions = result;
    } catch {
      return null;
    }
  }

  private setInitialMCode() {
    this.isSubscribedAll = false;
    this.currentMCode = this.mCodes.split(',')[0];
    this.getNewsletterInfo(this.currentMCode);
  }

  private async getNewsletterInfo(mCode: string) {
    try {
      const response = await fetch(
        `${this.baseUrlValue}/api/authex/site/newsletters?mCode=${mCode}`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );
      const result = await response.json();
      this.newsletterInfo = result;
    } catch {
      this.loading = false;
      this.infoError = true;
    }
  }

  private async getDisclosureInfo() {
    try {
      const response = await fetch(
        `${this.baseUrlValue}/api/authex/site/disclosures/${
          this.mpid || 23708
        }`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );
      const result = await response.json();
      this.disclosureInfo = result;
    } catch {
      this.loading = false;
      this.infoError = true;
    }
  }

  handleSubmit = async (event: Event) => {
    event.preventDefault();

    const data = new FormData(event.target as any);

    this.loading = true;

    const baseUrl =
      this.baseUrl ||
      (this.local ? 'http://localhost:4200' : window.location.origin);

    try {
      await fetch(`${baseUrl}/api/authex/user/newsletters?action=optIn`, {
        method: 'POST',
        body: JSON.stringify({
          emailAddress: data.get('emailAddress'),
          masterProductId: `${this.mpid || 23708}`,
          mCode: this.currentMCode,
          source: this.source,
          disclosures: this.disclosureInfo.map((i) => i.disclosureId)
        })
      });
      this.isSubscribed = true;
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  };

  private handleInView() {
    const pid = this.getCookieValue('pid');
    if (pid) {
      this.getUserSubscriptions().then((result) => {
        if (!result) {
          this.setInitialMCode();
        }
      });
    } else {
      this.setInitialMCode();
    }

    this.getDisclosureInfo();
    this.fetched = true;
  }

  componentWillLoad() {
    this.baseUrlValue =
      this.baseUrl ||
      (this.local
        ? 'http://localhost:4200'
        : window.location.origin !== 'null' && !!window.location.origin
        ? window.location.origin
        : 'https://www.cbssports.com');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.fetched) {
            this.handleInView();
          }
        });
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    observer.observe(this.el);
  }

  @Watch('newsletterInfo')
  watchNewsletterInfo(newValue: any) {
    if (newValue.title && this.disclosureInfo.length > 0) {
      this.loading = false;
    }
  }

  @Watch('disclosureInfo')
  watchDisclosureInfo(newValue: any) {
    if (newValue.length > 0 && this.newsletterInfo.title) {
      this.loading = false;
    }
  }

  @Watch('userSubscriptions')
  watchUserSubscriptions(newValue: any) {
    const mCodes: string[] = this.mCodes.split(',');

    if (
      mCodes.every((mCode) => {
        return newValue?.subscriptions.some((sub) => sub.mCode === mCode);
      })
    ) {
      this.loading = false;
      return (this.isSubscribedAll = true);
    } else {
      this.isSubscribedAll = false;
      const subscribedMcodes = new Set(
        newValue?.subscriptions.map((sub) => sub.mCode)
      );
      const firstNonSubscribedMcode = mCodes.find(
        (code) => !subscribedMcodes.has(code)
      );
      if (firstNonSubscribedMcode) {
        this.currentMCode = firstNonSubscribedMcode;
        this.getNewsletterInfo(this.currentMCode);
      } else {
        this.isSubscribedAll = true;
      }
    }
  }

  render() {
    if (this?.dataType === 'article') {
      return (
        <section
          class={`Newsletter-overrides Newsletter-container Newsletter-container--marker ${
            this.isSubscribedAll || this.infoError ? 'Hidden' : 'Flex'
          }`}
          data-mobile-app={this.mobileApp ? 'true' : 'false'}
          data-type={this.dataType}
        >
          {!this.isSubscribed ? (
            this.loading ? (
              <div class="loader-div">
                <div class="loader" />
              </div>
            ) : this.error ? (
              <div class="Newsletter-stateFail">
                <h3 class="Newsletter-title">Sorry!</h3>
                <h4 class="Newsletter-description">
                  There was an error processing your subscription.
                </h4>
              </div>
            ) : (
              <div class="Newsletter-stateNotSent">
                <div class="Newsletter-header">
                  <div class="Newsletter-image">
                    <figure class="img">
                      <img
                        alt={`${this.newsletterInfo?.author} newsletter icon`}
                        data-lazy={this.newsletterInfo?.image}
                        src={this.newsletterInfo?.image}
                        height="70"
                        width="70"
                      />
                    </figure>
                  </div>
                  <div class="Newsletter-texts">
                    <h5 class="Newsletter-intro">
                      {this.newsletterInfo?.author}
                    </h5>
                    <h4 class="Newsletter-description">
                      {this.newsletterInfo?.description}
                    </h4>
                  </div>
                </div>
                <form
                  id="Newsletter-overrides-form"
                  class="Newsletter-form Form-base"
                  name="NewsletterBanner"
                  onSubmit={this.handleSubmit}
                >
                  <p class="Newsletter-checkbox"></p>
                  <div class="CheckButton-newsletter">
                    {this.disclosureInfo?.map(
                      (
                        { disclosureText, requiredInd, disclosureId },
                        index
                      ) => (
                        <div class="CheckButton" key={index}>
                          <input
                            id={`CheckButton-checkbox-${disclosureId}`}
                            class="CheckButton-checkbox"
                            type="checkbox"
                            name="check"
                            required={requiredInd === 1}
                          />
                          <label
                            class="CheckButton-text"
                            data-component="checkboxExpanded"
                            data-checkbox-expanded-options='{"checkboxId":"CheckButton-checkbox","tableIds":""}'
                            htmlFor={`CheckButton-checkbox-${disclosureId}`}
                          >
                            <span>{disclosureText}</span>
                          </label>
                        </div>
                      )
                    )}
                  </div>
                  <div class="Newsletter-optIn">
                    By pressing sign up, I confirm that I have read and agree to
                    the{' '}
                    <a
                      class="Newsletter-optInLink"
                      href="https://www.paramount.com/terms-of-use"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms of Use{' '}
                    </a>
                    and agree to Paramount's{' '}
                    <a
                      class="Newsletter-optInLink"
                      href="https://privacy.paramount.com/en/policy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy{' '}
                    </a>
                    .
                  </div>
                  <div class="Newsletter-inputForm">
                    <input
                      type="text"
                      name="emailAddress"
                      class="Newsletter-emailAddress Form-inputText"
                      placeholder="Email Address"
                      required
                    />
                    <input
                      type="submit"
                      class="Newsletter-submit Form-submitButton--primary"
                      value="Sign Up"
                    />
                  </div>
                </form>
              </div>
            )
          ) : (
            <div class="Newsletter-stateSent">
              <div class="Newsletter-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  width={30}
                  height={30}
                >
                  <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" />
                </svg>
              </div>
              <h3 class="Newsletter-title">Thanks for signing up!</h3>
              <h4 class="Newsletter-description">Keep an eye on your inbox.</h4>
            </div>
          )}
        </section>
      );
    }
    return (
      <section
        class={`Newsletter-overrides Newsletter-container ${
          this.isSubscribedAll || this.infoError ? 'Hidden' : 'Flex'
        }`}
        data-type={this.dataType}
      >
        {!this.isSubscribed ? (
          this.loading ? (
            <div class="loader-div">
              <div class="loader" />
            </div>
          ) : this.error ? (
            <div class="Newsletter-stateFail">
              <h3 class="Newsletter-title">Sorry!</h3>

              <h4 class="Newsletter-description">
                There was an error processing your subscription.
              </h4>
            </div>
          ) : (
            <div class="Newsletter" id="Newsletter">
              <form
                id="Newsletter-overrides-form"
                class="Newsletter-form Form-base"
                name="Newsletter"
                onSubmit={this.handleSubmit}
              >
                <div class="Newsletter-stateNotSent">
                  {this.newsletterInfo?.image && (
                    <div class="Newsletter-image">
                      <figure class="img">
                        <img
                          alt={`${this.newsletterInfo?.author} newsletter icon`}
                          data-lazy={this.newsletterInfo?.image}
                          src={this.newsletterInfo?.image}
                          height="100"
                          width="100"
                          pinger-seen="true"
                        />
                      </figure>
                    </div>
                  )}
                  <div class="Newsletter-header">
                    <div class="Newsletter-texts">
                      <h3 class="Newsletter-title">
                        {this.newsletterInfo?.author}
                      </h3>
                      <h4 class="Newsletter-description">
                        {this.newsletterInfo?.description}
                      </h4>
                    </div>
                  </div>
                  <p class="Newsletter-checkbox"></p>
                  <div class="CheckButton CheckButton-newsletter">
                    {this.disclosureInfo?.map(
                      (
                        { disclosureText, disclosureId, requiredInd },
                        index
                      ) => (
                        <div class="CheckButton" key={index}>
                          <input
                            id={`CheckButton-checkbox-${disclosureId}`}
                            class="CheckButton-checkbox"
                            type="checkbox"
                            name="check"
                            required={requiredInd === 1}
                            value={this.checked ? 'on' : 'off'}
                            onChange={() => (this.checked = !this.checked)}
                          />
                          <label
                            class="CheckButton-text"
                            data-component="checkboxExpanded"
                            data-checkbox-expanded-options='{"checkboxId":"CheckButton-checkbox","tableIds":""}'
                            htmlFor={`CheckButton-checkbox-${disclosureId}`}
                          >
                            <span>{disclosureText}</span>
                          </label>
                        </div>
                      )
                    )}
                  </div>

                  <div class="Newsletter-optIn">
                    By pressing sign up, I confirm that I have read and agree to
                    the{' '}
                    <a
                      class="Newsletter-optInLink"
                      href="https://www.paramount.com/terms-of-use"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms of Use
                    </a>{' '}
                    and agree to Paramount's{' '}
                    <a
                      class="Newsletter-optInLink"
                      href="https://privacy.paramount.com/en/policy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </div>

                  <div class="Newsletter-inputForm">
                    <input
                      type="text"
                      name="emailAddress"
                      class="Newsletter-emailAddress Form-inputText"
                      placeholder="Email Address"
                      required
                    />
                    <input
                      type="submit"
                      class="Newsletter-submit Form-submitButton--primary"
                      value="Sign Up"
                    />
                  </div>
                </div>
                <div class="Newsletter-seeAll">
                  <a href={`${this.baseUrlValue}/newsletters/`}>
                    See All Newsletters
                  </a>
                </div>
              </form>
            </div>
          )
        ) : (
          <div class="Newsletter-stateSent">
            <div class="Newsletter-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                width={30}
                height={30}
              >
                <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" />
              </svg>
            </div>

            <h3 class="Newsletter-title">Thanks for signing up!</h3>

            <h4 class="Newsletter-description">Keep an eye on your inbox.</h4>
          </div>
        )}
      </section>
    );
  }
}
